import { ProductStatusBadge } from "@/components";
import { Alert, Button, Empty, Input, Select, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import { ArrowRightIcon, Clock3Icon, FileTextIcon, MapPinIcon, RefreshCwIcon, SearchIcon, SparklesIcon } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import { usePortalAiStatusQuery } from "../actions/assistant.actions";
import {
  PortalTripRequestStatus,
  usePortalCountriesQuery,
  usePortalTripRequestDashboardQuery,
} from "../actions/itinerary.actions";
import { PortalAccountMenu } from "../components/PortalAccountMenu";
import { PortalPageShell } from "../components/PortalProduct";
import type { PortalWorkspaceContext } from "../components/PortalWorkspaceLayout";
import { getPortalPaths } from "../lib/routes";

const PROMPT_SUGGESTIONS = [
  "Honeymoon · 6 nights",
  "Family of 4 · 7 nights",
  "Group of 20 · 4 nights",
  "Duplicate my last trip for new dates",
];

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
};

/** White metric card used by the dashboard's status row. */
const cardClassName = "rounded-xl border border-[#E8E2D4] bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};
const getFirstName = (name: string) => name.trim().split(/\s+/)[0] || "there";
const statusTone = (status: PortalTripRequestStatus): "gold" | "neutral" | "success" | "danger" => {
  if (status === "confirmed") return "success";
  if (status === "cancelled") return "danger";
  if (status === "submitted" || status === "returned") return "gold";
  return "neutral";
};

/** Quote status labels and tints shown on the dashboard cards. */
const STATUS_CARDS = [
  { key: "draft", label: "Saved quotes", note: "Ready to edit", tone: "text-[#0F766E]" },
  { key: "returned", label: "Needs attention", note: "Returned by", tone: "text-[#B45309]" },
  { key: "submitted", label: "Confirmed", note: "Sent to", tone: "text-[#0F766E]" },
  { key: "confirmed", label: "Booking started", note: "Approved for booking", tone: "text-[#0F766E]" },
  { key: "cancelled", label: "Cancelled", note: "Closed trips", tone: "text-gray-500" },
] as const;

/** Renders request-level agency metrics and work requiring attention. */
const PortalDashboardPage = () => {
  const { actor, organisationName, signOut } = useOutletContext<PortalWorkspaceContext>();
  const paths = getPortalPaths();
  const navigate = useNavigate();
  const [brief, setBrief] = useState("");
  const [searchText, setSearchText] = useState("");
  const [destination, setDestination] = useState<string | undefined>();
  const query = usePortalTripRequestDashboardQuery();
  const aiStatus = usePortalAiStatusQuery();
  const countriesQuery = usePortalCountriesQuery();
  // Only when AI is switched off for this agency do quotes start from a destination that opens the prefilled trip
  // details. A temporary tenant-level problem (credits, setup) or a failed status check keeps the AI entry point,
  // and the assistant page explains what is wrong.
  const aiUnavailable = aiStatus.data?.aiEnabled === false;
  const startWithDestination = () => {
    if (destination) navigate(paths.newRequest, { state: { destination } });
  };
  const startTrip = (value: string) => navigate(paths.assistant, { state: { brief: value.trim() } });
  const searchQuotes = () => {
    const term = searchText.trim();
    navigate(term ? `${paths.itineraries}?search=${encodeURIComponent(term)}` : paths.itineraries);
  };

  return (
    <PortalPageShell width="!max-w-none">
      <div className="space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">{dayjs().format("dddd, D MMM")}</p>
            <h1 className="portal-title text-2xl font-semibold text-[#131B2A]">
              {getGreeting()}, {getFirstName(actor.fullname)}
            </h1>
          </div>
          <div className="flex items-center gap-2.5">
            <form
              role="search"
              className="hidden md:block"
              onSubmit={(event) => {
                event.preventDefault();
                searchQuotes();
              }}
            >
              <Input
                aria-label="Search quotes, hotels and clients"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                prefix={<SearchIcon size={14} className="text-gray-400" />}
                placeholder="Search quotes, hotels, clients…"
                className="!h-10 !w-72 !rounded-xl !border-[#E8E2D4] !bg-white xl:!w-80"
                allowClear
              />
            </form>
            <Tooltip title="Refresh dashboard">
              <Button
                aria-label="Refresh dashboard"
                className="!h-10 !w-10 !rounded-xl !border-[#E8E2D4]"
                icon={<RefreshCwIcon size={15} />}
                loading={query.isFetching}
                onClick={() => void query.refetch()}
              />
            </Tooltip>
            <PortalAccountMenu
              actorName={actor.fullname}
              actorEmail={actor.email}
              agencyName={actor.agency?.name}
              profilePath={paths.profile}
              onSignOut={signOut}
            />
          </div>
        </header>

        <section
          aria-labelledby="dashboard-ai-heading"
          className="rounded-2xl bg-(--brand-secondary) px-5 py-6 text-white sm:px-7 sm:py-7"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-(--portal-accent)">
                Start a quotation
              </p>
              <h2 id="dashboard-ai-heading" className="portal-title mt-1.5 text-2xl font-bold text-white sm:text-3xl">
                {aiStatus.isLoading
                  ? "Start a new quotation"
                  : aiUnavailable
                    ? "Where is your client travelling?"
                    : "Tell the AI what your client wants"}
              </h2>
            </div>
            <Link
              to={paths.newRequest}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 px-3.5 py-2 text-xs font-semibold !text-white no-underline transition-colors hover:border-white/50 hover:bg-white/10 hover:!text-white"
            >
              Or build manually <ArrowRightIcon size={13} />
            </Link>
          </div>
          {aiStatus.isLoading ? (
            <div role="status" aria-busy="true" aria-label="Loading quotation options" className="mt-5 h-14 animate-pulse rounded-xl bg-white/10" />
          ) : aiUnavailable ? (
            <form
              className="mt-5 flex items-center gap-2 rounded-xl bg-white p-1.5 pl-4 shadow-[0_1px_2px_rgba(0,0,0,0.12)]"
              onSubmit={(event) => {
                event.preventDefault();
                startWithDestination();
              }}
            >
              <MapPinIcon size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
              <Select
                showSearch
                variant="borderless"
                value={destination}
                onChange={setDestination}
                loading={countriesQuery.isLoading}
                aria-label="Enter your destination"
                placeholder="Enter your destination"
                optionFilterProp="label"
                notFoundContent="No destination found"
                options={(countriesQuery.data || []).map((country) => ({ value: country, label: country }))}
                className="min-w-0 flex-1 !text-[15px]"
              />
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                disabled={!destination}
                icon={<ArrowRightIcon size={15} />}
                className="!h-11 shrink-0 !rounded-lg !px-5 !font-semibold"
              >
                Start quote
              </Button>
            </form>
          ) : (
            <>
          <form
            className="mt-5 flex items-center gap-2 rounded-xl bg-white p-1.5 pl-4 shadow-[0_1px_2px_rgba(0,0,0,0.12)]"
            onSubmit={(event) => {
              event.preventDefault();
              startTrip(brief);
            }}
          >
            <Input.TextArea
              aria-label="Describe the trip"
              value={brief}
              onChange={(event) => setBrief(event.target.value)}
              onPressEnter={(event) => {
                if (event.shiftKey) return;
                event.preventDefault();
                startTrip(brief);
              }}
              variant="borderless"
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={1000}
              placeholder="e.g. Family of 4, 7 nights in December, Hanoi – Ha Long – Hoi An, 4★, veg meals"
              className="!min-h-10 flex-1 !resize-none !px-0 !text-[15px] !text-[#131B2A]"
            />
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              icon={<SparklesIcon size={15} />}
              className="!h-11 shrink-0 !rounded-lg !border-(--portal-accent) !bg-(--portal-accent) !px-5 !font-semibold !text-(--portal-accent-ink) hover:!border-(--portal-accent-hover) hover:!bg-(--portal-accent-hover)"
            >
              Generate quote
            </Button>
          </form>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {PROMPT_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setBrief(suggestion)}
                className="rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-medium text-white/90 transition-colors hover:border-white/50 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                {suggestion}
              </button>
            ))}
          </div>
            </>
          )}
        </section>

        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load the trips dashboard"
            description="Refresh to try again."
            action={
              <Button size="small" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        <section aria-label="Trips by status" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {STATUS_CARDS.map(({ key, label, note, tone }) => (
            <Link
              key={key}
              to={`${paths.itineraries}?status=${key}`}
              className={`${cardClassName} group block !bg-white p-5 no-underline transition-shadow hover:shadow-[0_6px_18px_rgba(19,27,42,0.08)]`}
            >
              <span className="block text-xs text-gray-600">{label}</span>
              {query.isLoading ? (
                <Skeleton.Button active size="small" className="!mt-2" />
              ) : (
                <span className="portal-title mt-1.5 block text-4xl font-bold tabular-nums text-[#131B2A]">
                  {query.data?.counts[key] ?? "–"}
                </span>
              )}
              <span className={`mt-1 block text-xs font-medium ${tone}`}>
                {key === "returned" || key === "submitted" ? `${note} ${organisationName}` : note}
              </span>
            </Link>
          ))}
        </section>

        {query.data?.returned.length ? (
          <section className="overflow-hidden rounded-xl border border-(--portal-warn-border) bg-(--portal-warn-bg)">
            <div className="flex items-center justify-between border-b border-(--portal-warn-border) px-5 py-3.5">
              <div>
                <h2 className="text-sm font-semibold text-(--portal-warn-ink)">Returned trips need attention</h2>
                <p className="mt-0.5 text-xs text-(--portal-warn-ink)/80">
                  Review {organisationName}&apos;s reason, update a quote, and confirm again.
                </p>
              </div>
              <Link to={`${paths.itineraries}?status=returned`} className="text-xs font-semibold !text-(--portal-warn-ink)">
                View all
              </Link>
            </div>
            <ul className="divide-y divide-(--portal-warn-border)">
              {query.data.returned.map((request) => (
                <li key={request.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#131B2A]">
                      {request.guestName || "Unnamed guest"} · {request.reference}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-(--portal-warn-ink)">
                      {request.portalReturnReason || `${organisationName} returned this trip for changes.`}
                    </p>
                  </div>
                  <Link to={paths.request(request.id)}>
                    <Button type="primary" icon={<SparklesIcon size={14} />}>
                      Continue editing
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(320px,0.9fr)]">
          <section className={`${cardClassName} overflow-hidden`}>
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-base font-semibold text-[#131B2A]">Recent quotations</h2>
              <Link to={paths.itineraries} className="text-xs font-semibold !text-(--brand-secondary) no-underline">
                View all
              </Link>
            </div>
            {query.isLoading ? (
              <div className="space-y-4 p-5">
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} active paragraph={{ rows: 1 }} />
                ))}
              </div>
            ) : query.data?.recent.length ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="bg-[#FAF8F3] text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                      <th className="px-5 py-2.5 font-semibold">Request</th>
                      <th className="px-3 py-2.5 font-semibold">Trip</th>
                      <th className="px-3 py-2.5 font-semibold">Pax</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Value</th>
                      <th className="px-3 py-2.5 font-semibold">Status</th>
                      <th className="px-5 py-2.5 font-semibold">Guest name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0EBDF]">
                    {query.data.recent.map((request) => (
                      <tr
                        key={request.id}
                        className="cursor-pointer transition-colors hover:bg-[#FAF8F3]"
                        onClick={() => navigate(paths.request(request.id))}
                      >
                        <td className="px-5 py-3.5">
                          <Link
                            to={paths.request(request.id)}
                            className="font-semibold !text-(--brand-secondary) no-underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {request.reference}
                          </Link>
                        </td>
                        <td className="px-3 py-3.5 text-gray-600">
                          {request.summary
                            ? `${request.summary.destinations.join(" · ")} · ${request.summary.nights}N`
                            : "No active quote"}
                        </td>
                        <td className="px-3 py-3.5 tabular-nums text-gray-600">
                          {request.summary?.travellers.total ?? "–"}
                        </td>
                        <td className="px-3 py-3.5 text-right font-medium tabular-nums text-[#131B2A]">
                          {request.summary?.totalPrice
                            ? formatMoney(request.summary.totalPrice.amount, request.summary.totalPrice.currency)
                            : "–"}
                        </td>
                        <td className="px-3 py-3.5">
                          <ProductStatusBadge tone={statusTone(request.requestStatus.key)}>
                            {request.requestStatus.label}
                          </ProductStatusBadge>
                        </td>
                        <td className="px-5 py-3.5 text-[#131B2A]">{request.guestName || "Unnamed guest"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-10">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No trips yet">
                  <Link to={paths.newRequest}>
                    <Button type="primary">Create your first trip</Button>
                  </Link>
                </Empty>
              </div>
            )}
          </section>

          <section className={`${cardClassName} overflow-hidden`}>
            <div className="px-5 pb-1 pt-4">
              <h2 className="text-base font-semibold text-[#131B2A]">Itinerary operations</h2>
              <p className="mt-0.5 text-xs text-gray-500">Booking work stays separate from request totals.</p>
            </div>
            <div className="divide-y divide-[#F0EBDF]">
              <Link
                to={`${paths.itineraries}?bookingStatus=in_progress`}
                className="group flex items-center gap-3 px-5 py-4 no-underline transition-colors hover:!bg-[#FAF8F3]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--portal-warn-bg) text-(--portal-warn-ink)">
                  <Clock3Icon size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#131B2A]">Track active bookings</span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Review itineraries with booking work in progress.
                  </span>
                </span>
                <ArrowRightIcon size={14} className="text-gray-300 group-hover:text-(--brand-secondary)" />
              </Link>
              <Link
                to={paths.itineraries}
                className="group flex items-center gap-3 px-5 py-4 no-underline transition-colors hover:!bg-[#FAF8F3]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-(--portal-warn-bg) text-(--portal-warn-ink)">
                  <FileTextIcon size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#131B2A]">View all itineraries</span>
                  <span className="mt-0.5 block text-xs text-gray-500">Search and review every itinerary option.</span>
                </span>
                <ArrowRightIcon size={14} className="text-gray-300 group-hover:text-(--brand-secondary)" />
              </Link>
            </div>
          </section>
        </div>
      </div>
    </PortalPageShell>
  );
};

export default PortalDashboardPage;
